import { PreconditionFailedError } from "./errors";
import type { Exam } from "./exam.model";
import type { ChoiceFindingContent, FindingDefinition, MeasuredFindingContent } from "./finding-definition.model";
import type { DomainId } from "./lib/domain-id";
import type { MemberId } from "./member-identity";
import type { PatientId } from "./patient.model";
import type { PractitionerId } from "./practitioner.model";
import type { Procedure, ProcedureId } from "./procedure.model";
import type { VersionedCanonical } from "./spine.model";
import { type AnsweredValue, answeredValueProblem } from "./value.model";
import type { VisitId } from "./visit.model";

// ============================================================
// Finding — a patient-owned judgement recorded in a visit
//
// Each record pins the definition offered to its clinician. Edits replace the
// record; patient change creates another record. Retraction preserves history.
// Unanswered is a comment written before the clinician has an answer; it
// documents nothing and exists only to carry that comment.
// Exam membership belongs to the panel and never changes this ownership.
// ============================================================

export type FindingRecordId = DomainId<"FindingRecordId">;
export function asFindingRecordId(value: string): FindingRecordId {
  if (!value.trim()) throw new PreconditionFailedError("A Finding identity cannot be empty");
  return value as FindingRecordId;
}

type ComponentValue = AnsweredValue & { componentId: MemberId };
/** A measured value as recorded by a caller. Its label is the definition's to assign, never the caller's. */
export type FindingComponentValue = ComponentValue & { label?: never };
/** A stored measured value, labelled from the definition revision it was recorded against. */
export type FindingComponentAnswer = ComponentValue & { label: string };

type Answer<Value> = { kind: "Choice"; selections: readonly string[] } | { kind: "Measured"; values: readonly Value[] };
type State<Value> =
  | { kind: "Performed"; answer: Answer<Value> }
  | { kind: "DidNotPerform" }
  | { kind: "UnableToPerform" }
  | { kind: "Unanswered" };

/** What a performed finding answered, tagged with the definition kind it answers. */
export type FindingAnswer = Answer<FindingComponentAnswer>;
export type FindingState = State<FindingComponentAnswer>;
export type FindingAnswerInput = Answer<FindingComponentValue>;
export type FindingStateInput = State<FindingComponentValue>;

interface RecordingFields {
  visit: VisitId;
  clinician: PractitionerId;
  recordedAt: string;
  comment?: string;
  procedure?: ProcedureId;
}

export interface FindingRecording extends RecordingFields {
  state: FindingStateInput;
}

export interface FindingComponentChange {
  componentId: MemberId;
  /** Absent clears the component. */
  answer?: AnsweredValue;
  clinician: PractitionerId;
  recordedAt: string;
  /** Issued while the finding read DNP/UnableToPerform, so it intentionally replaces that absence. */
  replacesAbsence?: boolean;
}

export interface FindingFacts extends RecordingFields {
  id: FindingRecordId;
  state: FindingState;
  patient: PatientId;
  definition: VersionedCanonical;
  status: "final" | "enteredInError";
  /** Only a choice answer has one; absent on a measured finding. */
  cardinality?: "Single" | "Multiple";
  trackedForProgress: boolean;
}

export class Finding {
  private data: FindingFacts;

  constructor(input: FindingRecording & { id: FindingRecordId; patient: PatientId }, definition: FindingDefinition) {
    this.assertRecording(input, definition);
    if (!input.patient.trim()) throw new PreconditionFailedError("A finding requires a patient");
    const { visit, clinician, recordedAt, comment, procedure, patient } = input;
    this.data = structuredClone({
      visit,
      clinician,
      recordedAt,
      ...(comment === undefined ? {} : { comment }),
      ...(procedure ? { procedure } : {}),
      patient,
      state: labelled(input.state, definition),
      id: asFindingRecordId(input.id),
      definition: { url: definition.url, version: String(definition.version) },
      status: "final",
      ...(definition.content.kind === "Choice" ? { cardinality: definition.content.cardinality } : {}),
      trackedForProgress: false,
    });
  }

  static restore(facts: FindingFacts): Finding {
    const finding = Object.create(Finding.prototype) as Finding;
    finding.data = structuredClone(facts);
    return finding;
  }

  get id(): FindingRecordId {
    return this.data.id;
  }
  get facts(): FindingFacts {
    return structuredClone(this.data);
  }
  snapshot(): FindingFacts {
    return this.facts;
  }

  edit(recording: FindingRecording, definition: FindingDefinition): void {
    this.assertActive();
    this.assertRevision(definition);
    if (recording.visit !== this.data.visit)
      throw new PreconditionFailedError("A finding's recording visit cannot change");
    this.assertRecording(recording, definition);
    const { comment: _comment, procedure: _procedure, ...prior } = this.data;
    this.data = structuredClone({ ...prior, ...recording, state: labelled(recording.state, definition) });
  }

  /**
   * Merges one measured component, so components saved independently cannot
   * overwrite each other. DNP/UnableToPerform discards the change unless it was
   * issued against that absence (`replacesAbsence`): a save queued behind the
   * absence is stale, one made on it amends it. Clearing the last answer is
   * guarded like a retraction; the record survives only to carry a comment.
   */
  answerComponent(definition: FindingDefinition, change: FindingComponentChange, exams: readonly Exam[]): void {
    this.assertActive();
    this.assertRevision(definition);
    const content = definition.content;
    if (content.kind !== "Measured") throw new PreconditionFailedError("Only a measured finding has components");
    if (!content.components.some((component) => component.id === change.componentId))
      throw new PreconditionFailedError("Unknown component value");
    const { state } = this.data;
    if ((state.kind === "DidNotPerform" || state.kind === "UnableToPerform") && !change.replacesAbsence) return;
    const values: ComponentValue[] = (
      state.kind === "Performed" && state.answer.kind === "Measured" ? [...state.answer.values] : []
    ).filter((answer) => answer.componentId !== change.componentId);
    if (change.answer) values.push({ ...structuredClone(change.answer), componentId: change.componentId });
    if (!values.length) {
      if (state.kind === "Unanswered") return;
      for (const exam of exams) exam.assertFindingRetractable(this.data.id);
      if (!this.data.comment?.trim()) {
        this.retract();
        return;
      }
    }
    const recording: RecordingFields & { state: State<ComponentValue> } = {
      visit: this.data.visit,
      clinician: change.clinician,
      recordedAt: change.recordedAt,
      state: values.length ? { kind: "Performed", answer: { kind: "Measured", values } } : { kind: "Unanswered" },
      ...(this.data.comment === undefined ? {} : { comment: this.data.comment }),
      ...(this.data.procedure ? { procedure: this.data.procedure } : {}),
    };
    this.assertRecording(recording, definition);
    this.data = { ...this.data, ...recording, state: labelled(recording.state, definition) };
  }

  assertProcedure(procedure: Procedure): void {
    if (procedure.patientId !== this.data.patient)
      throw new PreconditionFailedError("A finding can only link a procedure for its patient");
  }

  retract(): void {
    this.assertActive();
    this.data.status = "enteredInError";
  }

  setTrackedForProgress(tracked: boolean): void {
    this.assertActive();
    this.data.trackedForProgress = tracked;
  }

  private assertRevision(definition: FindingDefinition): void {
    if (definition.url !== this.data.definition.url || String(definition.version) !== this.data.definition.version) {
      throw new PreconditionFailedError("An edit must use the finding's recorded definition revision");
    }
  }

  private assertActive(): void {
    if (this.data.status !== "final") throw new PreconditionFailedError("A retracted finding cannot be edited");
  }

  private assertRecording(
    recording: RecordingFields & { state: State<ComponentValue> },
    definition: FindingDefinition,
  ): void {
    if (!recording.visit.trim() || !recording.clinician.trim() || !Number.isFinite(Date.parse(recording.recordedAt))) {
      throw new PreconditionFailedError("A finding requires a recording visit, clinician, and time");
    }
    if (recording.state.kind === "Unanswered" && !recording.comment?.trim())
      throw new PreconditionFailedError("An unanswered finding requires a comment");
    if (recording.state.kind !== "Performed") return;
    const answer = recording.state.answer;
    const content = definition.content;
    if (answer.kind === "Measured" && content.kind === "Measured") assertMeasuredAnswer(answer, content);
    else if (answer.kind === "Choice" && content.kind === "Choice") assertChoiceAnswer(answer, content);
    else
      throw new PreconditionFailedError(
        `A ${answer.kind.toLowerCase()} answer does not match a ${content.kind.toLowerCase()} finding`,
      );
  }
}

/** Labels each measured value from the revision recorded against, so the stored record describes itself. */
function labelled(state: State<ComponentValue>, definition: FindingDefinition): FindingState {
  if (state.kind !== "Performed") return state;
  const { answer } = state;
  const content = definition.content;
  if (answer.kind === "Choice") return { kind: "Performed", answer };
  if (content.kind !== "Measured")
    throw new PreconditionFailedError("A measured answer does not match a choice finding");
  return {
    kind: "Performed",
    answer: {
      kind: "Measured",
      values: answer.values.map((value) => {
        const component = content.components.find((candidate) => candidate.id === value.componentId);
        if (!component) throw new PreconditionFailedError("Unknown component value");
        return { ...value, label: component.label };
      }),
    },
  };
}

function assertMeasuredAnswer(
  answer: Extract<Answer<ComponentValue>, { kind: "Measured" }>,
  content: MeasuredFindingContent,
): void {
  const values = answer.values;
  if (!values.length) throw new PreconditionFailedError("A measured finding requires at least one component value");
  if (new Set(values.map((value) => value.componentId)).size !== values.length)
    throw new PreconditionFailedError("Duplicate component value");
  values.forEach((value) => {
    const component = content.components.find((component) => component.id === value.componentId);
    if (!component) throw new PreconditionFailedError("Unknown component value");
    const problem = answeredValueProblem(component.value, value);
    if (problem) throw new PreconditionFailedError(`${component.label} ${problem}`);
  });
}

function assertChoiceAnswer(answer: Extract<FindingAnswer, { kind: "Choice" }>, content: ChoiceFindingContent): void {
  const selections = answer.selections;
  const offered = new Set<string>(content.options.map((option) => option.id));
  if (content.offerOther) offered.add("other");
  if (!selections.length || selections.some((selection) => !offered.has(selection))) {
    throw new PreconditionFailedError("A finding requires selections offered by its definition");
  }
  if (new Set(selections).size !== selections.length) throw new PreconditionFailedError("Duplicate finding selection");
  if (selections.length > 1 && content.cardinality === "Single") {
    throw new PreconditionFailedError("These finding selections cannot be combined");
  }
  const normal = content.options.find((option) => option.normal);
  if (normal && selections.length > 1 && selections.includes(normal.id)) {
    throw new PreconditionFailedError(
      `${normal.label} is the normal result and cannot be combined with another selection`,
    );
  }
}
